/* eslint-disable no-bitwise */

/**
 * WARNING WARNING WARNING WARNING WARNING WARNING WARNING
 *
 * Matched functionality between the Eventsync PWA and Console.
 *
 * Must be identical in both repos to work properly. If you change this,
 * change the paired in the other repo!
 *
 * WARNING WARNING WARNING WARNING WARNING WARNING WARNING
 */

/**
 * Base32 alphabet as defined by RFC 4648
 * Uses uppercase letters A-Z and digits 2-7 (32 characters total)
 * Each character represents 5 bits of data
 */
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

/**
 * Converts a string to a Base32-encoded code of specified length
 * Uses SHA-256 hashing to ensure consistent, deterministic output
 *
 * @param input - The input string to encode
 * @param length - Desired length of the output code (default: 8)
 * @returns Promise<string> - Base32-encoded code
 */
export async function getStringToBase32Code(
  input: string,
  length = 8,
): Promise<string | undefined> {
  if (!input || length <= 0) {
    return undefined
  }

  // Generate SHA-256 hash of input to ensure deterministic, unique output
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  )
  // Convert hash buffer to byte array for easier bit manipulation
  const hashArray = new Uint8Array(hashBuffer)

  // Initialize Base32 encoding variables
  let result = '' // Final Base32 string being built
  let buffer = 0 // Bit accumulator for Base32 encoding
  let bufferLength = 0 // Number of bits currently in buffer

  // Process each byte from the hash array
  for (const byte of hashArray) {
    // Add current byte to bit buffer (shift left 8 bits, OR with new byte)
    buffer = (buffer << 8) | byte
    bufferLength += 8

    // Extract 5-bit chunks from buffer and convert to Base32 characters
    // Base32 uses 5 bits per character, so we need at least 5 bits
    while (bufferLength >= 5 && result.length < length) {
      bufferLength -= 5
      // Extract top 5 bits as index into Base32 alphabet
      const index = (buffer >> bufferLength) & 0x1f // 0x1f = 31 = 5 bits mask
      result += BASE32_ALPHABET[index]
    }
  }

  // Handle any remaining bits in buffer (less than 5 bits)
  if (result.length < length && bufferLength > 0) {
    // Pad remaining bits to create final 5-bit chunk
    const paddedBits = buffer << (5 - bufferLength)
    result += BASE32_ALPHABET[paddedBits & 0x1f]
  }

  // If we need more characters than the hash can provide (>52 chars),
  // extend deterministically using current result length as seed
  while (result.length < length) {
    const seed = result.length.toString()
    // Create pseudo-random value from seed using polynomial rolling hash
    const extendedValue = [...seed].reduce(
      (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, // >>> 0 ensures 32-bit unsigned
      0,
    )
    // Use modulo to get valid Base32 alphabet index
    result += BASE32_ALPHABET[extendedValue % 32]
  }

  // Return result truncated to exact requested length
  return result.slice(0, length)
}
